import React, { useContext, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import useInitialData from 'src/hooks/useInitialData';
import MobileSidenavAndButton from 'src/components/MobileSidenavAndButton';
import { CartButton, CartPopup } from 'src/components/CartPopup';
import DestinationDropdown from './DestinationDropdown';
import AboutDropdown from './AboutDropdown';
import ProfileDropdown from './ProfileDropdown';
import { AppRoutes } from 'src/lib/routes';
import { groupBy, sortBy } from 'lodash-es';
import HeaderSearch from 'src/components/HeaderSearch';
import { ControlHeaderPageNavContext } from 'src/contexts/ControlHeaderPageNavProvider';
import classNames from 'classnames';
import useCurrentUser from 'src/hooks/useCurrentUser';
import CurrencySelector from 'src/components/CurrencySelector';
import { useRouter } from 'next/router';
import ClientOnlyComponent from './ClientOnlyComponent';
import LogoImg from 'public/images/logo.png';
import ImageWithFallback from './ImageWithFallback';
import styles from 'src/styles/modules/components/header.module.scss';
import GiftDropdown from './GiftDropdown';
import { useTranslations } from 'next-intl';
import LocaleSelector from 'src/components/LocaleSelector';

const HIDE_HEADER_FILTER_ROUTES = [
  '/cities/[id]',
  '/countries/[id]',
  '/collections/[id]',
  '/collections/[id]/[cateId]'
];

// pages where the header floats over the hero instead of sticking above it
const FLOATING_HEADER_ROUTES = ['/'];

const Header: React.FC = () => {
  const translation = useTranslations('components.Header')
  const { showHeader, setShowHeader, hideHeaderFilter, setHideHeaderFilter } = useContext(ControlHeaderPageNavContext);
  const [currentUser] = useCurrentUser();
  const { countries, cities } = useInitialData();
  const router = useRouter();
  const groupedCities = {
    ...groupBy(sortBy(cities.filter(o => o.optionsCount > 0), ['name']), (city) => city.countryId),
    0: sortBy(cities.filter((city) => city.featured), [(o) => -o.optionsCount]),
  };
  const [hideAllPopups, setHideAllPopups] = useState(false)
  const [showCartPopup, setShowCartPopup] = useState(false);
  const isFloating = FLOATING_HEADER_ROUTES.includes(router.pathname);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<number>();
  const isTransparent = isFloating && !isScrolled && !isHovered && !showCartPopup;

  useEffect(() => {
    if (!isFloating) return;
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isFloating]);

  useEffect(() => () => window.clearTimeout(hoverTimeoutRef.current), []);

  useEffect(() => {
    if (router.isReady) {
      const toggle = () => {
        setShowHeader(true);
        setShowCartPopup(false);
      };
      router.events.on('routeChangeComplete', toggle);
      return () => {
        router.events.off('routeChangeComplete', toggle);
      }
    }
  }, [router, setShowHeader]);

  useEffect(() => {
    if (router.isReady) {
      const hideFilterHeader = () => {
        if (HIDE_HEADER_FILTER_ROUTES.includes(router.pathname)) {
          setHideHeaderFilter(true);
        } else {
          setHideHeaderFilter(false);
        }
      };
      hideFilterHeader();
      router.events.on('routeChangeComplete', hideFilterHeader);
      return () => {
        router.events.off('routeChangeComplete', hideFilterHeader);
      };
    }
  }, [router, setHideHeaderFilter]);

  const loginPath = AppRoutes.loginRoute(
    {},
    router.pathname === AppRoutes.loginRoute().toPath()
      ? {}
      : { redirect_path: router.pathname, redirect_path_query: JSON.stringify(router.query) }
  ).toPath();

  return (
    <>
      <div
        id="js-header"
        className={classNames('tw-top-0 tw-w-full tw-left-0 tw-z-100 tw-bg-transparent tw-border-0 tw-font-styleSans tw-font-semibold', {
          'tw-sticky': showHeader && !isFloating,
          'tw-fixed': showHeader && isFloating,
          'tw-invisible': !showHeader,
          [styles.floating]: isFloating,
          [styles.transparent]: isTransparent,
        })}
        // (mouse only: a tap on a phone counts as entering, but leaving needs a tap outside)
        onPointerEnter={(e) => {
          if (!isFloating || e.pointerType !== 'mouse') return;
          window.clearTimeout(hoverTimeoutRef.current);
          setIsHovered(true);
        }}
        onPointerLeave={(e) => {
          if (!isFloating || e.pointerType !== 'mouse') return;
          // going back to see-through waits a moment, so brushing past the header doesn't flicker
          hoverTimeoutRef.current = window.setTimeout(() => setIsHovered(false), 60);
        }}
      >
        <div className={classNames(styles.header, { 'tw-bg-brand-yellow': !isFloating, 'tw-shadow-md': !isTransparent || !isFloating })}>
          <div className="container tw-max-w-full">
            <nav className="navbar navbar-primary tw-justify-between tw-py-3 md:tw-py-4 tw-px-0 tw-flex-nowrap">
              <div className="tw-flex tw-flex-grow-0 tw-flex-shrink-0 tw-items-center">
                <div className="tw-hidden lg:tw-block">
                  <Link legacyBehavior prefetch={false}                     href={AppRoutes.rootRoute({}).toPath()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <a className="navbar-brand tw-p-0">
                      <ImageWithFallback src={LogoImg} loading="eager" className={`tw-static tw-inline-block tw-h-10 tw-w-auto ${styles.logo}`} alt="Seek Sophie" />
                    </a>
                  </Link>
                </div>

                <div className="tw-mr-4 tw-hidden lg:tw-block" onMouseEnter={() => setHideAllPopups(true)}>
                  <DestinationDropdown countries={countries} cities={groupedCities} />
                </div>

                <div className="tw-mr-4 tw-hidden xl:tw-block" onMouseEnter={() => setHideAllPopups(true)}>
                  <GiftDropdown />
                </div>

                <div className="tw-mr-4 tw-hidden xl:tw-block" onMouseEnter={() => setHideAllPopups(true)}>
                  <AboutDropdown />
                </div>

                <div className="tw-hidden lg:tw-block">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.magazinesRoute().toPath()}>
                    <a className="tw-text-current">
                      <span>{translation('feelNotes')}</span>
                    </a>
                  </Link>
                </div>
              </div>

              <div className="tw-relative tw-flex tw-items-center tw-flex-grow tw-justify-between lg:tw-justify-start tw-mx-0 md:tw-mx-5" onMouseEnter={() => setHideAllPopups(false)}>
                <div className="tw-flex tw-items-center">
                  <ClientOnlyComponent>
                    <div className="tw-mr-3 tw-block lg:tw-hidden">
                      <MobileSidenavAndButton
                        currentUser={currentUser}
                        cities={groupedCities}
                        countries={countries}
                      />
                    </div>
                  </ClientOnlyComponent>

                  <div className="tw-block lg:tw-hidden tw-mr-4">
                    <CartButton
                      isShowCart={showCartPopup}
                      onClick={() => setShowCartPopup(!showCartPopup)}
                    />
                  </div>
                </div>
              </div>

              <div className="tw-block tw-absolute tw-transform tw--translate-x-1/2 tw-left-1/2 lg:tw-hidden">
                <Link legacyBehavior prefetch={false}                     href={AppRoutes.rootRoute({}).toPath()}
                  target="_blank"
                  rel="noreferrer"
                >
                  <a className="tw-p-0">
                    <ImageWithFallback src={LogoImg} loading="eager" className={`tw-static tw-inline-block tw-h-10 tw-w-auto ${styles.logo}`} alt="Seek Sophie" />
                  </a>
                </Link>
              </div>

              <div className="tw-flex tw-flex-grow-0 tw-flex-shrink-0 tw-items-center">
                <div className="md:tw-mr-4">
                  <HeaderSearch
                    hideAllPopups={hideAllPopups}
                    isTransparent={isFloating ? isTransparent : undefined}
                  />
                </div>

                <div className="tw-hidden lg:tw-block tw-mr-4">
                  <CartButton
                    isShowCart={showCartPopup}
                    onClick={() => setShowCartPopup(!showCartPopup)}
                  />
                </div>

                <div className="tw-hidden lg:tw-block tw-mr-4">
                  <LocaleSelector
                    onClick={() => {
                      setShowCartPopup(false);
                    }}
                  />
                </div>

                {currentUser ? (
                  <div className="tw-hidden lg:tw-block" onMouseEnter={() => setHideAllPopups(true)}>
                    <ProfileDropdown isHeaderTransparent={isTransparent} />
                  </div>
                ) : (
                  <div className="tw-hidden lg:tw-block">
                    <Link legacyBehavior prefetch={false} href={loginPath}>
                      <a className="tw-text-current tw-font-styleSans tw-font-semibold">{translation('login')}</a>
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>

        <div id="extra-header"></div>
      </div>
      <CartPopup
        isShowCart={showCartPopup}
        onClick={() => setShowCartPopup(false)}
      />
    </>
  );
};

export default Header;
